import type { SystemDetailsData } from '../types';

const SOURCE_LABELS: Record<string, string> = {
  gas: 'Gas boiler',
  oil: 'Oil boiler',
  lpg: 'LPG boiler',
  electric: 'Electric',
  'heat-pump': 'Heat pump',
  hybrid: 'Hybrid',
  unknown: 'Not assessed yet',
};

const ARCH_LABELS: Record<string, string> = {
  'combi-on-demand': 'Combi (on-demand)',
  'stored-dhw': 'Stored DHW',
  unknown: 'Not assessed yet',
};

const CIRCUIT_LABELS: Record<string, string> = {
  sealed: 'Sealed (system boiler)',
  'open-vented': 'Open-vented (regular boiler)',
  unknown: 'Not assessed yet',
};

const TOPOLOGY_LABELS: Record<string, string> = {
  'y-plan': 'Y-plan',
  's-plan': 'S-plan',
  's-plan-plus': 'S-plan+',
  combi: 'Combi (no valve plan)',
  'gravity-dhw': 'Gravity DHW',
  unknown: 'Not assessed yet',
};

const EMITTER_LABELS: Record<string, string> = {
  radiators: 'Radiators',
  ufh: 'UFH',
  mixed: 'Mixed (rads + UFH)',
  unknown: 'Not assessed yet',
};

const CYLINDER_SPACE_LABELS: Record<string, string> = {
  'yes-existing': 'Yes (existing location)',
  'yes-available': 'Yes (space available)',
  no: 'No space',
  unknown: 'Not assessed yet',
};

const TEMP_CONTROLS_LABELS: Record<string, string> = {
  basic: 'Basic',
  'programmable-stat': 'Programmable stat',
  'load-compensation': 'Load compensation',
  smart: 'Smart',
  'smart-load-compensation': 'Smart load compensation',
  unknown: 'Not assessed yet',
};

interface Props {
  data: SystemDetailsData;
  onClick: () => void;
}

export function SystemDetailsTile({ data, onClick }: Props) {
  const v = (val: string, map: Record<string, string>) => map[val] ?? val ?? '—';
  const isUnknown = (val: string) => !val || val === 'unknown';

  const lowTempLabel = data.lowTempDesign === 'yes' ? 'Yes' : data.lowTempDesign === 'no' ? 'No' : 'Not assessed yet';

  return (
    <div className="hub-tile" onClick={onClick} role="button" tabIndex={0}>
      <div className="hub-tile__header">
        <h3 className="hub-tile__title">System Details</h3>
        <span className={`hub-tile__badge hub-tile__badge--${data.confidence.level}`}>
          {data.confidence.level}
        </span>
      </div>
      <div className="hub-tile__body">
        <div className="hub-tile__row">
          <span className="hub-tile__label">Heat source</span>
          <span className={`hub-tile__value${isUnknown(data.heatSource) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.heatSource, SOURCE_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Architecture</span>
          <span className={`hub-tile__value${isUnknown(data.dhwArchitecture) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.dhwArchitecture, ARCH_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">CH circuit</span>
          <span className={`hub-tile__value${isUnknown(data.circuitType) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.circuitType, CIRCUIT_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Controls</span>
          <span className={`hub-tile__value${isUnknown(data.controlTopology) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.controlTopology, TOPOLOGY_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Emitters</span>
          <span className={`hub-tile__value${isUnknown(data.emitters) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.emitters, EMITTER_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Cylinder space</span>
          <span className={`hub-tile__value${isUnknown(data.cylinderSpace) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.cylinderSpace, CYLINDER_SPACE_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Low-temp design</span>
          <span className={`hub-tile__value${isUnknown(data.lowTempDesign) ? ' hub-tile__value--unknown' : ''}`}>
            {lowTempLabel}
            {data.lowTempEvidence && ` (${data.lowTempEvidence})`}
          </span>
        </div>
        {data.temperatureControls && data.temperatureControls !== 'unknown' && (
          <div className="hub-tile__row">
            <span className="hub-tile__label">Temperature controls</span>
            <span className="hub-tile__value">
              {v(data.temperatureControls, TEMP_CONTROLS_LABELS)}
            </span>
          </div>
        )}
        {(data.sedbukBand || data.sedbukRating || data.gcNumber) && (
          <div className="hub-tile__row">
            <span className="hub-tile__label">SEDBUK rating</span>
            <span className="hub-tile__value">
              {data.sedbukBand && `Band ${data.sedbukBand}`}
              {data.sedbukRating && ` (${data.sedbukRating}%)`}
              {data.gcNumber && !data.sedbukBand && !data.sedbukRating && `GC: ${data.gcNumber}`}
            </span>
          </div>
        )}
        {data.boilerOutput && (
          <div className="hub-tile__row">
            <span className="hub-tile__label">Boiler output</span>
            <span className="hub-tile__value">
              {(() => {
                const uppercaseOutput = data.boilerOutput.toUpperCase();
                return uppercaseOutput.includes('KW') ? uppercaseOutput : `${data.boilerOutput}kW`;
              })()}
            </span>
          </div>
        )}
        {data.systemAge && (
          <div className="hub-tile__row">
            <span className="hub-tile__label">System age</span>
            <span className="hub-tile__value">{data.systemAge} years</span>
          </div>
        )}
      </div>
      <span className="hub-tile__tap-hint">tap to edit</span>
    </div>
  );
}
